/* eslint   */

import React, {
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import some from 'lodash/some';

import { isBrowser } from '@utils/isBrowser';
import { retrieveAccessToken, validateToken } from '@utils/auth';

if (isBrowser()) {
  window.dataLayer = window.dataLayer || [];
}

const PAGE_SITE_PORTAL = 'lucid-motors-dashboard';
const PAGE_SITE_WWW = 'lucidmotors-www';

/* eslint-disable no-unused-vars */
const removeEmptyAttributes = obj =>
  Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));

const pushToDataLayer = obj => {
  obj = removeEmptyAttributes(obj);
  if (process.env.GATSBY_HOSTING_ENVIRONMENT !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('analytics debug:\n', JSON.stringify(obj, null, 2));
  }
  if (isBrowser()) {
    window.dataLayer.push(obj);
  }
};

export const AnalyticsContext = React.createContext({
  trackPageView: () => {},
  trackInteraction: () => {},
  trackProcess: () => {},
  trackError: () => {},
});

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error(
      'useAnalytics must be used as a child of AnalyticsProvider.'
    );
  }
  return context;
};

export const AnalyticsProvider = ({ pathname, children }) => {
  const [gclid, setGCLID] = useState('');

  const {
    userProfile: {
      user: {
        email,
        orders,
        vehicles,
        activeVehicle: { index: activeVehicleIndex },
      },
    },
  } = useSelector(state => state);

  const currentVehicleStatus = useMemo(() => {
    if (!vehicles.length) {
      return '';
    }
    switch (vehicles[activeVehicleIndex]?.status) {
      case 'FULFILLED':
        return 'owned';
      case 'RESERVATION':
      case 'ORDER_ELIGIBLE':
      case 'ORDER_PROCESSING':
        return 'reserved';
      case 'ORDER':
      case 'FULLY_PAID':
        return 'ordered';
      default:
        return '';
    }
  }, [vehicles, activeVehicleIndex]);

  const customerType = useMemo(
    () => (some(orders) ? 'owner' : 'non-owner'),
    [orders]
  );
  const currentPageSections = useMemo(() => {
    const splitPaths = (pathname?.split('/') || []).filter(
      pathname => !!pathname.trim()
    );
    if (splitPaths[0] === 'portal') {
      splitPaths.shift();
    }

    return splitPaths;
  }, [pathname]);

  // Methods.
  const trackPageView = useCallback(
    (
      page_view__pageSite,
      page_view__pageSection1,
      page_view__pageSection2,
      page_view__pageDescription,
      page_view__customerType,
      page_view__currentVehicleStatus,
      page_view__loginStatus
    ) => {
      if (!page_view__pageSite) {
        const isPortal = pathname?.match(/^\/portal\/.+/m)?.length;
        page_view__pageSite = isPortal ? PAGE_SITE_PORTAL : PAGE_SITE_WWW;
      }

      if (page_view__pageSite === PAGE_SITE_WWW) {
        if (!page_view__loginStatus) {
          page_view__loginStatus = validateToken(retrieveAccessToken())
            ? 'yes'
            : 'no';
        }
      } else if (page_view__pageSite === PAGE_SITE_PORTAL) {
        if (!page_view__customerType) {
          page_view__customerType = customerType;
        }
        if (!page_view__currentVehicleStatus) {
          page_view__currentVehicleStatus = currentVehicleStatus;
        }
      }

      if (page_view__pageSection1 === undefined) {
        page_view__pageSection1 = currentPageSections[0] || '';
      }

      if (page_view__pageSection2 === undefined) {
        page_view__pageSection2 = currentPageSections[1];
      }

      if (!page_view__pageDescription) {
        page_view__pageDescription =
          currentPageSections[1] || currentPageSections[0];
      }

      if (page_view__pageSite) {
        pushToDataLayer({
          event: 'page_view',
          page_view__pageSite,
          page_view__pageSection1,
          page_view__pageSection2,
          page_view__pageDescription,
          page_view__customerType,
          page_view__currentVehicleStatus,
          page_view__loginStatus,
        });
      }
    },
    [customerType, currentVehicleStatus, currentPageSections]
  );

  const trackInteraction = useCallback(
    (
      interaction__type,
      interaction__text,
      interaction__destination,
      interaction__component,
      interaction__designOrderStatus
    ) => {
      if (interaction__component === 'reservations-orders')
        interaction__designOrderStatus = currentVehicleStatus;
      pushToDataLayer({
        event: 'interaction',
        interaction__type,
        interaction__text,
        interaction__destination,
        interaction__component,
        interaction__designOrderStatus,
      });
    },
    []
  );

  const trackProcess = (process__name, process__step, process__parameters) =>
    pushToDataLayer({
      event: 'process',
      process__name,
      process__step,
      process__parameters,
    });

  useEffect(() => {
    const gclid = new URLSearchParams(
      location?.search || window?.location?.search
    )?.get('gclid');
    if (gclid) {
      setGCLID(gclid);
    }
  }, []);

  const trackError = () => {};

  useEffect(() => {
    trackPageView();
  }, [pathname, email, activeVehicleIndex]);

  return (
    <AnalyticsContext.Provider
      value={{
        trackPageView,
        trackInteraction,
        trackProcess,
        trackError,
        gclid,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useUnload = fn => {
  const cb = React.useRef(fn);
  useEffect(() => {
    const onUnload = cb.current;
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [cb]);
};
